import React, {
  useMemo,
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
} from "react";
import { useAtom } from "jotai";
import { defaultCols } from "pages/ProposalList/columns";
import { ExpandedColumn } from "library/types/columns";
import { LoadStatus, Setting } from "library/types/types";
import { hydrateColumns } from "library/utils/columns";
import { APP_ID } from "library/constants";
import { handleAxiosError } from "library/utils";
import { toast } from "react-toastify";
import i18next from "i18next";
import { useAppSettingsContext } from "contexts/AppSettingsProvider";
import api from "service/api";
import Axios from "axios";
import {
  proposalsPageOfferItemsLoadStatusState,
  proposalsPageRowsState,
  proposalsPageTotalState,
} from "store/ProposalsPageStore";
import { hydrateOffers } from "store/ProposalsPageHooks";

interface IMainContext {
  // Column Management
  /** Applied columns: they drive the grid, the search query and the exports. */
  columns: ExpandedColumn[];
  onSetColumns: (columns: ExpandedColumn[]) => void;
  /** Draft columns edited in the column options panel. */
  tempColumns: ExpandedColumn[];
  onSetTempColumns: (columns: ExpandedColumn[]) => void;
  onUpdateTempColumns: (
    update: (columns: ExpandedColumn[]) => ExpandedColumn[]
  ) => void;
  /** Applies the draft (columns and view) and returns the applied columns. */
  applyDraft: () => ExpandedColumn[];
  /** Drops the draft and goes back to the applied columns and view. */
  discardDraft: () => void;

  // View Management
  /** The view selected in the panel (draft); null means the default columns. */
  selectedView: Setting | null;
  /** The saved columns of the selected view, or the default columns. */
  selectedViewColumns: ExpandedColumn[];
  selectedViewId: string | number | null;
  /** Selects a view in the panel and loads its columns into the draft. */
  onSetSelectedViewId: (value: string | number | null) => void;

  // Settings Management
  columnOptionsSettings: Setting[];
  createSetting: (newSetting: Setting, onSuccess?: () => void) => Promise<void>;
  deleteSetting: (id: number, onSuccess?: () => void) => Promise<void>;
  updateSetting: (setting: Setting, onSuccess?: () => void) => Promise<void>;
  loadStatus: LoadStatus;

  // Add loading states
  appSettingsLoaded: boolean;
  columnsReady: boolean;
}

type Props = {
  children: React.ReactNode;
};

export const MainContext = createContext<IMainContext | undefined>(undefined);

export const useMainContext = (): IMainContext => {
  const context = useContext(MainContext);

  if (!context) {
    throw new Error("useMainContext cannot be used without MainContext");
  }

  return context;
};

/** The saved columns of a view, or the default columns when there is no view
 * or its data cannot be read. */
const viewColumns = (view: Setting | null): ExpandedColumn[] => {
  if (!view) return defaultCols;
  try {
    return hydrateColumns({
      columns: JSON.parse(view.userAppSettingValue) as ExpandedColumn[],
      defaultColumns: defaultCols,
    });
  } catch {
    return defaultCols;
  }
};

export const MainProvider = ({ children }: Props) => {
  // Get settings and mutations from AppSettingsProvider
  const {
    settings: appSettings,
    isInitialized: appSettingsLoaded,
    mutations: { addMutation, updateMutation, deleteMutation },
  } = useAppSettingsContext();

  // State
  const [columns, setColumns] = useState<ExpandedColumn[]>(defaultCols);
  const [tempColumns, setTempColumns] = useState<ExpandedColumn[]>(defaultCols);
  // The view selected in the panel, and the view whose columns are applied.
  const [selectedViewId, setSelectedViewId] = useState<string | number | null>(
    null
  );
  const [appliedViewId, setAppliedViewId] = useState<string | number | null>(
    null
  );
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("none");
  const [columnsReady, setColumnsReady] = useState(false);

  // Jotai atoms for initial load results
  const [, setOfferItemsLoadStatus] = useAtom(proposalsPageOfferItemsLoadStatusState);
  const [, setRows] = useAtom(proposalsPageRowsState);
  const [, setTotal] = useAtom(proposalsPageTotalState);
  // Track if we've initialized the default view to prevent resetting on settings changes
  const hasInitializedView = React.useRef(false);

  // Computed values
  const columnOptionsSettings = useMemo(() => {
    // Convert IUserSettings[] to Setting[] format and filter
    // Create new object references to ensure React detects changes
    return (appSettings as Setting[])
      .filter((s) => {
        return (
          s.appSettingCodeId === 900000000010303 &&
          s.applicationCodeId === APP_ID
        );
      })
      .map((s) => ({ ...s })); // Create new object references
  }, [appSettings]);

  const findView = useCallback(
    (viewId: string | number | null) =>
      viewId === null
        ? null
        : columnOptionsSettings.find((s) => s.userAppSettingId === viewId) ??
          null,
    [columnOptionsSettings]
  );

  // Always resolved from the current settings, so a saved, renamed or deleted
  // view is reflected immediately.
  const selectedView = useMemo(
    () => findView(selectedViewId),
    [findView, selectedViewId]
  );
  const selectedViewColumns = useMemo(
    () => viewColumns(selectedView),
    [selectedView]
  );

  // Callbacks
  const onSetColumns = useCallback((columns: ExpandedColumn[]) => {
    setColumns(columns);
  }, []);

  const onSetTempColumns = useCallback((columns: ExpandedColumn[]) => {
    setTempColumns(columns);
  }, []);

  const onUpdateTempColumns = useCallback(
    (update: (columns: ExpandedColumn[]) => ExpandedColumn[]) => {
      setTempColumns(update);
    },
    []
  );

  // Switching views only changes the draft; the grid changes on apply.
  const onSetSelectedViewId = useCallback(
    (viewId: string | number | null) => {
      const view = findView(viewId);
      setSelectedViewId(view ? view.userAppSettingId : null);
      setTempColumns(viewColumns(view));
    },
    [findView]
  );

  const applyDraft = useCallback(() => {
    setColumns(tempColumns);
    setAppliedViewId(selectedViewId);
    return tempColumns;
  }, [tempColumns, selectedViewId]);

  const discardDraft = useCallback(() => {
    setTempColumns(columns);
    setSelectedViewId(findView(appliedViewId) ? appliedViewId : null);
  }, [columns, appliedViewId, findView]);

  // Settings API functions - use AppSettingsProvider mutations
  const createSetting = useCallback(
    async (newSetting: Setting, onSuccess?: () => void) => {
      try {
        setLoadStatus("loading");
        addMutation.mutate([newSetting as any], {
          onSuccess: (data: any) => {
            setLoadStatus("success");
            // Find the new setting ID from response
            const newSettingId = data.find(
              (s: any) => s.userAppSettingName === newSetting.userAppSettingName
            )?.userAppSettingId;
            // The new view holds the draft columns, so only the selection changes.
            if (newSettingId) {
              setSelectedViewId(newSettingId);
            }
            toast.success(i18next.t("greco.success"));
            onSuccess?.();
          },
          onError: (err: any) => {
            handleAxiosError(err);
            setLoadStatus("error");
          },
        });
      } catch (err) {
        handleAxiosError(err);
        setLoadStatus("error");
      }
    },
    [addMutation]
  );

  const deleteSetting = useCallback(
    async (id: number, onSuccess?: () => void) => {
      try {
        setLoadStatus("loading");
        deleteMutation.mutate([id], {
          onSuccess: () => {
            setLoadStatus("success");
            if (id === selectedViewId) {
              setSelectedViewId(null);
              setTempColumns(defaultCols);
            }
            toast.success(i18next.t("greco.success"));
            onSuccess?.();
          },
          onError: (err: any) => {
            handleAxiosError(err);
            setLoadStatus("error");
          },
        });
      } catch (err) {
        handleAxiosError(err);
        setLoadStatus("error");
      }
    },
    [deleteMutation, selectedViewId]
  );

  const updateSetting = useCallback(
    async (setting: Setting, onSuccess?: () => void) => {
      try {
        setLoadStatus("loading");
        // Use the AppSettingsProvider mutation which updates state immediately
        updateMutation.mutate([setting as any], {
          onSuccess: () => {
            setLoadStatus("success");
            toast.success(i18next.t("greco.success"));
            onSuccess?.();
          },
          onError: (err: any) => {
            handleAxiosError(err);
            setLoadStatus("error");
          },
        });
      } catch (err) {
        handleAxiosError(err);
        setLoadStatus("error");
      }
    },
    [updateMutation]
  );

  // Effects

  // 1. Initialize view + columns in one pass when settings load,
  //    then immediately fire the initial search with the correct columns.
  useEffect(() => {
    if (!appSettingsLoaded || hasInitializedView.current) return;
    hasInitializedView.current = true;

    let initialColumns = defaultCols;
    const defaultViewSetting = columnOptionsSettings.find((s) => s.isDefault);

    if (defaultViewSetting) {
      setSelectedViewId(defaultViewSetting.userAppSettingId);
      setAppliedViewId(defaultViewSetting.userAppSettingId);
      initialColumns = viewColumns(defaultViewSetting);
      setColumns(initialColumns);
      setTempColumns(initialColumns);
    } else {
      setSelectedViewId(null);
      setAppliedViewId(null);
    }

    // Fire initial search right here — columns are a local variable, no timing issues.
    const cancelSource = Axios.CancelToken.source();
    setOfferItemsLoadStatus("loading");
    api
      .searchOffers({
        columns: initialColumns,
        skip: 0,
        top: 40,
        search: "",
        sort: { key: "", dir: "" },
        cancelToken: cancelSource.token,
        productFilter: "all",
      })
      .then((response) => {
        setRows(hydrateOffers(response.data.value));
        setTotal(response.data["@odata.count"]);
        setOfferItemsLoadStatus("success");
        // Mark columns ready AFTER data is loaded, so ProposalList's
        // hasInitialLoad is set only after data is in place.
        setColumnsReady(true);
      })
      .catch((error: any) => {
        if (error.code !== "ERR_CANCELED") {
          handleAxiosError(error);
          setOfferItemsLoadStatus("error");
        }
        setColumnsReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSettingsLoaded, columnOptionsSettings]);

  // View switching is handled imperatively by onSetSelectedViewId above.
  // No cascading effects needed.

  const value = useMemo(
    () => ({
      columns,
      onSetColumns,
      tempColumns,
      onSetTempColumns,
      onUpdateTempColumns,
      applyDraft,
      discardDraft,
      selectedView,
      selectedViewColumns,
      selectedViewId,
      onSetSelectedViewId,
      columnOptionsSettings,
      createSetting,
      deleteSetting,
      updateSetting,
      loadStatus,
      appSettingsLoaded,
      columnsReady,
    }),
    [
      columns,
      onSetColumns,
      tempColumns,
      onSetTempColumns,
      onUpdateTempColumns,
      applyDraft,
      discardDraft,
      selectedView,
      selectedViewColumns,
      selectedViewId,
      onSetSelectedViewId,
      columnOptionsSettings,
      createSetting,
      deleteSetting,
      updateSetting,
      loadStatus,
      appSettingsLoaded,
      columnsReady,
    ]
  );

  return <MainContext.Provider value={value}>{children}</MainContext.Provider>;
};
